import React, { useState } from "react";
import axios from "axios";
import { 
  FaEnvelope, FaPhone, FaFileUpload, FaBriefcase, FaPaperPlane,
  FaBroom, FaBaby, FaBriefcaseMedical, FaCar, FaUserNurse, FaWheelchair, 
  FaIdCard, FaCheckCircle, FaArrowRight 
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useColorMode, Box, Spinner } from "@chakra-ui/react";
import { motion } from "framer-motion";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).toLowerCase());
const validatePhone = (phone) => {
  const cleaned = phone.replace(/[\s.\-()+]/g, '');
  return /^(33|0)[0-9]{9}$/.test(cleaned);
};

const RecrutementPage = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    jobTitle: "",
    email: "",
    phone: "",
    cv: null,
  });

  const availableJobs = [
    { title: "Assistant(e) ménager(ère)", icon: FaBroom, desc: "Responsable de l'entretien et de l'hygiène des espaces de vie.", tags: ["Ménage", "Hygiène"] },
    { title: "Garde d'enfant(s)", icon: FaBaby, desc: "Accompagnement éducatif et sécurité des plus jeunes au domicile.", tags: ["Enfance", "Éducation"] },
    { title: "Aide-soignant(e)", icon: FaBriefcaseMedical, desc: "Assistance médicale de proximité et soins de confort.", tags: ["Santé", "Médical"] },
    { title: "Chauffeur(se) PMR", icon: FaCar, desc: "Transport sécurisé pour personnes à mobilité réduite.", tags: ["Transport", "PMR"] },
    { title: "Auxiliaire de vie", icon: FaUserNurse, desc: "Soutien quotidien et maintien de l'autonomie des seniors.", tags: ["Seniors", "Autonomie"] },
    { title: "Intervenant(e) Handicap", icon: FaWheelchair, desc: "Accompagnement spécifique pour les bénéficiaires.", tags: ["Handicap", "Social"] }
  ];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file) {
        const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowed.includes(file.type)) {
          setErrors(prev => ({ ...prev, cv: "Format non accepté (PDF, DOC, DOCX uniquement)" }));
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setErrors(prev => ({ ...prev, cv: "Fichier trop volumineux (5 Mo maximum)" }));
          return;
        }
        setErrors(prev => ({ ...prev, cv: "" }));
      }
      setFormData(prev => ({ ...prev, [name]: file }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const scrollToForm = (title) => {
    setFormData(prev => ({ ...prev, jobTitle: title }));
    const element = document.getElementById("apply-form");
    if (element) {
        element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (formData.jobTitle.trim().length < 3) newErrors.jobTitle = "Minimum 3 caractères";
    if (!validateEmail(formData.email)) newErrors.email = "Adresse email invalide";
    if (formData.phone && !validatePhone(formData.phone)) newErrors.phone = "Format invalide (ex: 06 12 34 56 78)";
    if (!formData.cv) newErrors.cv = "Veuillez joindre votre CV";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setLoading(true);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const data = new FormData();
    data.append("jobTitle", formData.jobTitle);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("cv", formData.cv); 
    data.append("type", "RECRUTEMENT");

    try {
      await axios.post(`${API_URL}/api/contact/recrutement`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Candidature envoyée !");
      setFormData({ jobTitle: "", email: "", phone: "", cv: null });
      if(document.getElementById("cv")) document.getElementById("cv").value = "";
    } catch (error) {
      toast.error("Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#FAFCFF] min-h-screen'}>
      <Helmet>
        <title>Recrutement — Rejoignez Hetep Iaout Services</title>
        <meta name="description" content="Rejoignez l'équipe HIS. Nous recrutons des auxiliaires de vie, aides à domicile et accompagnateurs certifiés en Île-de-France. Envoyez votre candidature." />
        <link rel="canonical" href="https://www.hisvie.com/recrutement/" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 pt-32 md:pt-40 pb-32">
        
        {/* --- HEADER --- */}
        <header className="mb-16 md:mb-24">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className={`text-5xl md:text-8xl font-black tracking-tighter leading-none mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                RECRUTE<span className="text-blue-600 font-serif italic font-light lowercase">ment.</span>
            </h1>
            <p className={`text-lg md:text-xl font-light max-w-2xl opacity-60 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Rejoignez une équipe engagée pour transformer l'accompagnement à domicile en une expérience d'exception.
            </p>
          </motion.div>
        </header>

        {/* --- JOB LIST --- */}
        <section className="space-y-4 mb-24 md:mb-32">
          {availableJobs.map((job, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onClick={() => scrollToForm(job.title)}
              className={`group flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 rounded-[2rem] border cursor-pointer transition-all duration-500 ${
                isDark ? 'bg-gray-900/40 border-gray-800 hover:border-blue-600 hover:bg-gray-900' : 'bg-white border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-600'
              }`}
            >
              <div className="flex items-center gap-4 md:gap-10">
                <div className={`w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-2xl flex items-center justify-center text-xl transition-colors ${isDark ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-600'} group-hover:bg-blue-600 group-hover:text-white`}>
                  <job.icon />
                </div>
                <div>
                  <h3 className={`text-lg md:text-2xl font-black mb-1 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{job.title}</h3>
                  <p className="text-xs md:text-sm font-light opacity-50 line-clamp-2 md:line-clamp-none">{job.desc}</p>
                </div>
              </div>

              <div className="mt-4 md:mt-0 flex items-center justify-between md:justify-end gap-6">
                <div className="hidden lg:flex gap-2">
                  {job.tags.map((tag, i) => (
                    <span key={i} className={`text-[9px] uppercase font-black tracking-widest px-4 py-2 rounded-full ${isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-blue-600 font-black text-[10px] uppercase tracking-widest md:opacity-0 md:group-hover:opacity-100 transition-all transform md:translate-x-4 md:group-hover:translate-x-0">
                  Postuler <FaArrowRight />
                </div>
              </div>
            </motion.div>
          ))}
        </section>

        {/* --- FORM SECTION (CORRIGÉE POUR MOBILE) --- */}
        <section id="apply-form" className="max-w-4xl mx-auto scroll-mt-20">
          <motion.div 
            className={`p-6 md:p-16 rounded-[2.5rem] md:rounded-[3rem] border ${isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-100 shadow-2xl shadow-blue-900/10'}`}
          >
            <div className="text-center mb-10 md:mb-12">
              <h2 className={`text-2xl md:text-3xl font-black uppercase tracking-tight mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Déposer mon dossier</h2>
              <p className="text-xs md:text-sm font-light opacity-50">Réponse garantie sous 48 heures ouvrées.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
              {/* Le Grid passe en 1 colonne sur mobile impérativement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                
                <div className="md:col-span-2 flex flex-col space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-wider md:tracking-widest opacity-40 px-2 block">
                    Poste souhaité
                  </label>
                  <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} maxLength={100} placeholder="Assistant(e) ménager(ère)..."
                    className={`w-full p-4 md:p-5 rounded-2xl border outline-none transition-all ${errors.jobTitle ? 'border-red-400 bg-red-50' : isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600'}`} />
                  {errors.jobTitle && <p className="text-red-500 text-xs px-2">{errors.jobTitle}</p>}
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-wider md:tracking-widest opacity-40 px-2 block">
                    Email professionnel
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} maxLength={100} placeholder="adresse@mail.com"
                    className={`w-full p-4 md:p-5 rounded-2xl border outline-none transition-all ${errors.email ? 'border-red-400 bg-red-50' : isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600'}`} />
                  {errors.email && <p className="text-red-500 text-xs px-2">{errors.email}</p>}
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-wider md:tracking-widest opacity-40 px-2 block">
                    Téléphone
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} maxLength={20} placeholder="06 12 34 56 78"
                    className={`w-full p-4 md:p-5 rounded-2xl border outline-none transition-all ${errors.phone ? 'border-red-400 bg-red-50' : isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600'}`} />
                  {errors.phone && <p className="text-red-500 text-xs px-2">{errors.phone}</p>}
                </div>

                <div className="md:col-span-2 flex flex-col space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-wider md:tracking-widest opacity-40 px-2 block">
                    Curriculum Vitae (PDF, DOC, DOCX — max 5 Mo)
                  </label>
                  <div className={`relative border-2 border-dashed rounded-[2rem] p-8 md:p-12 text-center transition-all ${errors.cv ? 'border-red-400 bg-red-50' : isDark ? 'border-gray-700 hover:border-blue-600 bg-gray-800/50' : 'border-gray-200 hover:border-blue-600 bg-gray-50'}`}>
                    <FaFileUpload className="mx-auto text-2xl md:text-3xl mb-3 md:mb-4 text-blue-600 opacity-20" />
                    <input type="file" id="cv" name="cv" accept=".pdf,.doc,.docx" onChange={handleChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                    <p className="text-[10px] md:text-xs font-bold opacity-60 break-words">
                      {formData.cv ? `Sélectionné : ${formData.cv.name}` : "Cliquez ici pour joindre votre CV"}
                    </p>
                  </div>
                  {errors.cv && <p className="text-red-500 text-xs px-2">{errors.cv}</p>}
                </div>
              </div>

              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit" 
                disabled={loading} 
                className="w-full py-5 md:py-6 bg-blue-600 text-white rounded-[2rem] font-black uppercase tracking-widest text-[10px] md:text-[11px] shadow-xl shadow-blue-600/20 flex items-center justify-center gap-4 transition-colors hover:bg-blue-700"
              >
                {loading ? <Spinner size="sm"/> : <><FaPaperPlane /> Envoyer ma candidature</>}
              </motion.button>
            </form>
          </motion.div>
        </section>
      </div>
    </Box>
  );
};

export default RecrutementPage;